import React, { createRef, useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import Truncate from 'react-truncate';
import truncateString from "../../utils/truncateString";
import {
  faEllipsisV,
  faEdit,
  faTrash,
  faBookmark,
  faWrench,
  faLock,
  faFileDownload
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  ButtonToolbar,
  DropdownButton,
  Dropdown,
  OverlayTrigger
} from "react-bootstrap";
// API Urls

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import DeleteConfirmForm from "../settings/DeleteConfirmForm";
import UploadModalContent from "./UploadModalContent";
import UpdateItemTagsForm from "./UpdateItemTagsForm";
import ButtonShareWithRecipient from "../ButtonShareWithRecipient";
import {
  setItem,
  upvoteItem,
  shareItem,
  deleteUpvoteItem,
  favoriteItem,
  deleteFavoriteItem,
  addTagToItem,
  removeTagFromItem,
  updateTagsForItems
} from "../../actions/items";
import { addContentTagSuccess } from '../../actions/settings';
import { copyToClipboard, MESSAGES, getThumbnailUrlForItem, addEventNameToEndpoint } from "../../utils/common";
import { ShareType } from "../../constants/Types";
import { getSimpleIconByName } from "../../assets/icons";
import iconUpvoteFilled from "../../assets/images/icon-upvote-filled.svg";
import iconUpvoteOutlined from "../../assets/images/icon-upvote-outline.svg";
import iconFavoriteFilled from "../../assets/images/icon-sq-24-star-filled.svg";
import iconFavoriteOutlined from "../../assets/images/icon-sq-24-star-outline.svg";

const ReactSwal = withReactContent(Swal);

const renderTooltip = (props, key) => {
  if (props.arrowProps) {
    delete props.arrowProps;
  }

  return (
    <div
      {...props}
      show={props.show.toString()}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        padding: '2px 10px',
        color: 'white',
        borderRadius: 3,
        ...props.style,
      }}
    >
      {key}
    </div>
  );
};

const ItemCard = props => {
  const [recipient, setRecipient] = useState('');
  const [shortInfo, setShortInfo] = useState(null);

  const shareLinkRef = createRef();

  useEffect(() => {
    const newShortInfo = _.get(props, "shortInfo", null);
    const thisItemId = _.get(props.data, "id", null);

    if (newShortInfo && newShortInfo.id.includes(thisItemId)) {
      setShortInfo(newShortInfo)
    }
  }, [props]);

  useEffect(() => {
    const didAlert = _.get(shortInfo, "didAlert", true);
    if (!didAlert) {
      copyAndNotifyRecipientShare();
    }
  }, [shortInfo]);

  const handleGotoDetail = (e) => {
    e.stopPropagation();
    const { data, history } = props;

    if (e.shiftKey) {
      if (props.onClick) {
        props.onClick(data.id);
      }
      return;
    }

    e.stopPropagation();
    props.setItem(data.id);
    history.push(`/item/detail/${data.id}`, {
      id: data.id,
      data
    });
  }

  const handleDeleteItem = async () => {
    ReactSwal.fire({
      allowOutsideClick: true,
      allowEscapeKey: true,
      allowEnterKey: false,
      showCancelButton: false,
      showConfirmButton: false,
      html: (
        <DeleteConfirmForm
          type='item'
          data={props.data}
          onClose={ReactSwal.clickCancel}
          onConfirm={ReactSwal.clickConfirm}
        />
      )
    })
    .then(result => {
      if (result.value) {
        props.deleteItem(props.data.id);
      }
    });
  }

  
  /**
   * Handle the basic short URL copy to clipboard operation
   * @param {Object} e 
   */
  const onClickShare = (e) => {
    e.stopPropagation();

    addEventNameToEndpoint(props.Analytics, 'user-shorturl-copy');
    copyToClipboard(_.get(props, 'data.urls.short', ''));
  }

  /**
   * Handle the short URL share with a recipient noted
   * along with the short URL
   * @param {Object} e 
   * @param {String} id itemId 
   */

  const onClickShareWithRecipient = async (e, id) => {
    // console.log({functionName: "onClickShareWithRecipient", e});
    e.stopPropagation();
    // dismiss overlay
    document.body.click();
    const sharedTo = recipient.trim();
    
    // just copy link if no recipient
    if (!sharedTo) {
      onClickShare(e);
      return;
    }

    // we have a recipient
    const shortUrl = _.get(props, 'data.urls.short', '');

    // clear alert status
    if (shortInfo) {
      shortInfo.didAlert = false;
    }
    props.shareItem(id, sharedTo, shortUrl, ShareType.Item);
  }

  const copyAndNotifyRecipientShare = () => {
    if (shortInfo && shortInfo.shareId) {
      const { shortUrl, shareId, id } = shortInfo;
      const trackingUrl = `${shortUrl}?sid=${shareId}`;
      copyToClipboard(trackingUrl, MESSAGES.URL_RECIPIENT_COPY);
      setShortInfo({ ...shortInfo, didAlert: true });

      // analytics
      addEventNameToEndpoint(props.Analytics, 'user-shorturl-share');
      props.Analytics.record({name: 'user-shorturl-share', attributes: { 
        itemId: id,
        shortUrl: shortUrl.replace("https://", ""),
      }});
    }
  }


  /**
   * Upvote or un-upvote the item
   * @param {Object} e 
   * @param {String} id 
   * @param {Boolen} isAddUpvote 
   */
  const handleUpvote = (e, id, isAddUpvote) => {
    e.stopPropagation();

    addEventNameToEndpoint(props.Analytics, isAddUpvote ? 'user-item-upvote' : 'user-item-unupvote');
    if (isAddUpvote) {
      props.upvoteItem(id);
    } else {
      props.deleteUpvoteItem(id);
    }
  }

  /**
   * Favorite or un-favorite an item
   * @param {Object} e 
   * @param {String} id  
   * @param {Boolean} isFavorite 
   */
  const handleFavorite = (e, id, isFavorite) => {
    e.stopPropagation();

    addEventNameToEndpoint(props.Analytics, isFavorite ? 'user-item-unfavorite' : 'user-item-favorite');
    if (isFavorite) {
      props.deleteFavoriteItem(id);
    } else {
      props.favoriteItem(id);
    }
  }

  const handleUpdateItem = () => {
    ReactSwal.fire({
      allowOutsideClick: true,
      allowEscapeKey: true,
      allowEnterKey: false,
      showCancelButton: false,
      showConfirmButton: false,
      html: (
        <UploadModalContent
          user={props.user}
          cognitoUser={props.cognitoUser}
          itemId={_.get(props.data, "id", null)}
          path={_.get(props.history, 'location.pathname', '')}
          onClose={ReactSwal.clickCancel}
          Auth={props.Auth}
          Analytics={props.Analytics}
        />
      )
    })
    .then(result => {
      if (result.value) {
        
      }
    });
  }

  const handleAddTags = () => {
    ReactSwal.fire({
      allowOutsideClick: true,
      allowEscapeKey: true,
      allowEnterKey: true,
      showCancelButton: false,
      showConfirmButton: false,
      html: (
        <UpdateItemTagsForm
          onClose={ReactSwal.clickCancel}
          onConfirm={ReactSwal.clickConfirm}
          itemData={props.data}
          cognitoUser={props.cognitoUser}
          Analytics={props.Analytics}
          contentTags={props.settings.contentTags}
          addContentTagSuccess={props.addContentTagSuccess}
          addTagToItem={props.addTagToItem}
          removeTagFromItem={props.removeTagFromItem}
          updateTagsForItems={props.updateTagsForItems}
          selectedItems={props.selectedItems}
        />
      )
    })
    .then(result => {
      if (result.value) {
        
      }
    });
  }

  const renderThumbnailImage = () => {
    let imageUrl = null;

    // mostly file upload images through transloadit
    imageUrl = getThumbnailUrlForItem(props.data);

    if (imageUrl) {
      return <img className="thumbnail-image" src={imageUrl} alt="no content" />;
    } else {
      return <div className="thumbnail-image no-thumbnail">{_.get(props.data, 'ext', '').toUpperCase()}</div>;
    }
  }

  const handleDragOver = e => {
    e.preventDefault();
    return false;
  }

  const handleDragStart = e => {
    e.dataTransfer.setData("item", JSON.stringify(props.data));
  }

  const { data } = props;

  // format the title
  const titleLong = _.get(data, 'meta.contentTitle', _.get(data, 'original.original_basename'));
  const titleShort = truncateString(titleLong, 59);
  const isFavorite = _.get(props, 'settings.userInfo.collectionFavorites', []).includes(data.id);
  const isAddUpvote = !_.get(props, 'settings.userInfo.collectionUpvotes', []).includes(data.id);
  const isShared = _.get(data, 'meta.isShared', true);
  const isAdmin = _.get(props, 'cognitoUser.attributes', {})['custom:role'] === 'admin';

  // URLs
  // source: links only, then try SSL and fallback to target 
  const downloadUrl = _.get(props, 'data.urls.source', 
    _.get(props, 'data.urls.ssl', 
    _.get(props, 'data.urls.target', '')));

  return (
    <div
      className={`item-card-container vertical ${isShared ? '' : 'locked'} ${props.isSelected ? 'selected-card' : ''}`}
      draggable={props.draggable || false}
      // onDrop={() => console.log('drop has happened')}
      // onDragEnd={() => console.log('item card drag end')}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
    >
      <div  
        className="detail-wrapper"
        onClick={handleGotoDetail}
      >
      <div className="thumbnail-box m-2">
        <DropdownButton
            drop="down"
            title={<FontAwesomeIcon icon={faEllipsisV} />}
            id="dropdown-button-drop-down"
            key="down"
            onClick={(e) => e.stopPropagation()}
          >
          {/* Context Menu  */}
          {isAdmin && <Dropdown.Item eventKey="1" onClick={handleAddTags}><FontAwesomeIcon icon={faEdit} className="mr-2" />Tag...</Dropdown.Item>}
          {isAdmin && <Dropdown.Item eventKey="1" onClick={handleGotoDetail}><FontAwesomeIcon icon={faEdit} className="mr-2" />Edit...</Dropdown.Item>}

          <Dropdown.Item eventKey="4" onClick={() => window.open(downloadUrl, "_blank")}><FontAwesomeIcon icon={faFileDownload} className="mr-2" />Download</Dropdown.Item>

          {isAdmin && <Dropdown.Item eventKey="2" onClick={handleUpdateItem}><FontAwesomeIcon icon={faWrench} className="mr-2" />Update...</Dropdown.Item>}

          {isAdmin && <Dropdown.Item eventKey="3" onClick={handleDeleteItem}><FontAwesomeIcon icon={faTrash} className="mr-2" />Delete</Dropdown.Item>}

        </DropdownButton>
        <div className="file-icon">
          <img src={getSimpleIconByName(_.get(data, 'ext', 'file'))} alt="file icon" />
        </div>
        {renderThumbnailImage()}
      </div>
      <div className="pl-3 pr-3 pb-3">
        <div className="top-actions">
          <div className="marks">
            {
              Object.keys(_.get(data, 'meta.contentTags', {})).map(key =>
                <div key={key}>
                  <OverlayTrigger
                    placement="right-start"
                    delay={{ show: 250, hide: 400 }}
                    overlay={props => renderTooltip(props, key)}
                  >
                    <FontAwesomeIcon
                      key={key}
                      icon={faBookmark}
                      rotation={270}
                      color={_.get(data, 'meta.contentTags', {})[key].color}
                      className="mr-1 ml-1"
                      onClick={e => {
                        e.stopPropagation();
                        props.onTagSelect(key);
                      }}
                    />
                  </OverlayTrigger>
                </div>
              )
            }
          </div>
        </div>
        <div className="file-description">
          <div className="description">
            <p className="details block-with-text" title={titleLong}>
              <Truncate lines={3} ellipsis={<span>...</span>}> {titleLong} </Truncate>
            </p>
            <div className="d-flex">
              {!isShared && <FontAwesomeIcon className="mr-2 lock-share" icon={faLock} />}
              <p className="document-type">{_.get(data, 'meta.contentType', '')}</p>
            </div>
          </div>
        </div>
        <div className="d-flex views-counts">
          <div className="rating">
            <div
              className="image-button"
              onClick={(e) => handleUpvote(e, _.get(props, 'data.id'), isAddUpvote)}
              data-cc-analytics-on='click'
              id={isAddUpvote ? 'btnUnupvoteItem' : 'btnUpvoteItem'}
              data-cc-analytics-name={isAddUpvote ? 'user-item-upvote' : 'user-item-unupvote'}
              data-cc-analytics-attrs={`itemId:${_.get(props, 'data.id')},name:${_.get(props, 'data.name', '')},userId:${_.get(props, 'data.userId', '')},path:/dashboard`}
            >
              <img src={isAddUpvote ? iconUpvoteOutlined : iconUpvoteFilled} alt="voted icon" />
            </div>
            <div className="dots">{_.get(data, 'meta.upvoteCount', 0)}</div>
          </div>

          <div className="stars">
            <img
              src={isFavorite ? iconFavoriteFilled: iconFavoriteOutlined}
              alt="favorite"
              className='mr-1'
              onClick={(e) => handleFavorite(e, _.get(props, 'data.id'), isFavorite)}
              data-cc-analytics-on='click'
              id={isFavorite ? 'btnUnfavoriteItem' : 'btnFavoriteItem'}
              data-cc-analytics-name={isFavorite ? 'user-item-unfavorite' : 'user-item-favorite'}
              data-cc-analytics-attrs={`itemId:${_.get(props, 'data.id')},name:${_.get(props, 'data.name', '')},userId:${_.get(props, 'data.userId', '')},path:/dashboard`}
            />
            <span className="star-count">{_.get(data, 'meta.favoriteCount', 0)}</span>
          </div>
        </div>
      </div>
      </div>
      <div className="bottom-actions" ref={shareLinkRef}>
        <ButtonToolbar className="buttons">
          <Button
            variant="outline-danger"
            className="btn btn-outline-danger"
            size="sm"
            onClick={() => window.open(downloadUrl, "_blank")}
            data-cc-analytics-on='click'
            id='btnItemCardDownload'
            data-cc-analytics-name='user-item-download'
            data-cc-analytics-attrs={`itemId:${data.id},shortUrl:${_.get(props, 'data.urls.short', '').replace('https://', '')}`}
          >
            <FontAwesomeIcon icon={faFileDownload} />
          </Button>
          {isShared &&
            <Button
              variant="outline-danger"
              className="btn btn-outline-danger"
              size="sm"
              onClick={onClickShare}
              data-cc-analytics-on='click'
              id='btnItemCardShare'
              data-cc-analytics-name='user-shorturl-copy'
              data-cc-analytics-attrs={`itemId:${data.id},shortUrl:${_.get(props, 'data.urls.short', '').replace('https://', '')}`}
            >
              LINK
            </Button>
          }
          {isShared &&
            <ButtonShareWithRecipient
              variant="outline-danger"
              itemId={_.get(data, "id")}
              itemShortUrl={_.get(data, "urls.short")}
              container={shareLinkRef}
              changeNotificationPermission={props.changeNotificationPermission}
              itemType={ShareType.Item}
            />
          }
        </ButtonToolbar>
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  settings: state.settings,
  cognitoUser: state.auth.cognitoUser,
  Analytics: state.auth.Analytics,
  shortInfo: state.item.shortInfo,
  Auth: state.auth.Auth,
  user: state.auth.tenant
});

const mapDispatchToProps = dispatch => 
bindActionCreators({
  setItem,
  upvoteItem,
  shareItem,
  deleteUpvoteItem,
  favoriteItem,
  deleteFavoriteItem,
  addContentTagSuccess,
  addTagToItem,
  removeTagFromItem,
  updateTagsForItems
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ItemCard);
